import React from "react";
import { usePrescriptionModule } from "./hooks/prescription_module.hook";
import { MedicineEntry } from "../../Common/interfaces/doctor.interfaces";
import style from "./prescription.module.css";

export const DoctorPrescription: React.FC = () => {
  const {
    consultation,
    activeTab,
    isSaving,
    diagnosis,
    medicines,
    selectedFile,
    filePreview,
    setActiveTab,
    setDiagnosis,
    setSelectedFile,
    setFilePreview,
    handleAddMedicine,
    handleRemoveMedicine,
    handleFieldChange,
    handleFileChange,
    handleSave,
    handleBack,
    toast,
  } = usePrescriptionModule();

  return (
    <div className={style.doctorBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "error"}
          </span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className={style.docHeader}>
        <button className={style.docHeaderBack} onClick={handleBack}>
          <span className="material-icons-round">arrow_back</span>
        </button>
        <div className={style.docHeaderTitle}>
          <h1>Prescription</h1>
          <span>{consultation?.ticket_id || "TKT-N/A"}</span>
        </div>
        <img
          src="/logo-transparent3.png"
          alt="Pococare"
          style={{ height: "36px" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
          }}
        />
      </div>

      <div className={style.formContent}>
        {/* Tab Switch */}
        <div className={style.tabSwitch}>
          <button className={activeTab === "digital" ? style.active : ""} onClick={() => setActiveTab("digital")}>
            Digital Entry
          </button>
          <button className={activeTab === "upload" ? style.active : ""} onClick={() => setActiveTab("upload")}>
            Upload Form
          </button>
        </div>

        {/* Digital Tab */}
        {activeTab === "digital" && (
          <div>
            {/* Patient Bar */}
            <div className={style.patientBar} style={{ marginBottom: "16px" }}>
              <div className={style.patientBarAvatar}>
                {consultation?.patient_name ? consultation.patient_name.slice(0, 2).toUpperCase() : "PT"}
              </div>
              <div className={style.patientBarInfo}>
                <div className={style.patientBarName}>{consultation?.patient_name || "Patient"}</div>
                <div className={style.patientBarMeta}>
                  {consultation?.patient_age || "N/A"} yrs • {consultation?.patient_gender || "N/A"}
                </div>
              </div>
            </div>

            {/* Diagnosis */}
            <div className={style.formGroup} style={{ marginBottom: "16px" }}>
              <label>Diagnosis</label>
              <input
                type="text"
                placeholder="Suspected Condition..."
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
              />
            </div>

            {/* Medicine Cards */}
            {medicines.map((med: MedicineEntry, idx: number) => (
              <div key={idx} className={style.medicineCard}>
                <div className={style.medicineHeader}>
                  <span className={style.medicineNumber}>Medicine {idx + 1}</span>
                  <button className={style.medicineRemove} onClick={() => handleRemoveMedicine(idx)}>
                    <span className="material-icons-round">close</span>
                  </button>
                </div>
                <div className={style.medicineFields}>
                  <div className={style.formGroup}>
                    <input
                      type="text"
                      placeholder="Medicine name"
                      value={med.medicine_name}
                      onChange={(e) => handleFieldChange(idx, "medicine_name", e.target.value)}
                    />
                  </div>
                  <div className={style.medicineRow}>
                    <div className={style.formGroup}>
                      <input
                        type="text"
                        placeholder="Dosage (e.g. 500mg)"
                        value={med.dosage}
                        onChange={(e) => handleFieldChange(idx, "dosage", e.target.value)}
                      />
                    </div>
                    <div className={style.formGroup}>
                      <input
                        type="text"
                        placeholder="Frequency (e.g. 1-0-1)"
                        value={med.frequency}
                        onChange={(e) => handleFieldChange(idx, "frequency", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={style.medicineRow}>
                    <div className={style.formGroup}>
                      <input
                        type="number"
                        placeholder="Duration (days)"
                        value={med.duration_days}
                        onChange={(e) => handleFieldChange(idx, "duration_days", Number(e.target.value))}
                      />
                    </div>
                    <div className={style.formGroup}>
                      <select
                        style={{
                          width: "100%", padding: "12px 14px",
                          border: "1.5px solid var(--outline)", borderRadius: "12px",
                          fontFamily: "'Inter', sans-serif", fontSize: "13px",
                          color: "var(--on-surface)", background: "var(--white)", outline: "none",
                        }}
                        value={med.timing}
                        onChange={(e) => handleFieldChange(idx, "timing", e.target.value)}
                      >
                        <option value="After Food">After Food</option>
                        <option value="Before Food">Before Food</option>
                        <option value="With Food">With Food</option>
                      </select>
                    </div>
                  </div>
                  <div className={style.formGroup}>
                    <input
                      type="text"
                      placeholder="Special Instructions"
                      value={med.instructions}
                      onChange={(e) => handleFieldChange(idx, "instructions", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button className={style.btnAddMedicine} onClick={handleAddMedicine}>
              <span className="material-icons-round" style={{ fontSize: "18px" }}>add</span>
              Add Medicine
            </button>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === "upload" && (
          <div className={style.uploadTab}>
            <input
              type="file"
              id="camera-upload-input"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <input
              type="file"
              id="file-upload-input"
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className={style.uploadActions}>
              <div className={style.uploadBtn} onClick={() => document.getElementById("camera-upload-input")?.click()}>
                <div className={style.uploadBtnIcon}><span className="material-icons-round">camera_alt</span></div>
                <span className={style.uploadBtnLabel}>Camera</span>
                <span className={style.uploadBtnHint}>Capture prescription</span>
              </div>
              <div className={style.uploadBtn} onClick={() => document.getElementById("file-upload-input")?.click()}>
                <div className={style.uploadBtnIcon}><span className="material-icons-round">photo_library</span></div>
                <span className={style.uploadBtnLabel}>Gallery / File</span>
                <span className={style.uploadBtnHint}>JPG, PNG, PDF</span>
              </div>
            </div>
            {selectedFile && (
              <div className={style.imagePreview}>
                <div className={style.previewHeader}>
                  <span className={style.previewHeaderTitle}>{selectedFile.name}</span>
                  <button
                    className={style.previewRemove}
                    onClick={() => { setSelectedFile(null); setFilePreview(null); }}
                  >
                    <span className="material-icons-round">delete</span>
                    Remove
                  </button>
                </div>
                {filePreview ? (
                  <img className={style.previewImg} src={filePreview} alt="Preview" />
                ) : (
                  <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--placeholder)" }}>
                    <span className="material-icons-round" style={{ fontSize: "48px" }}>picture_as_pdf</span>
                    <p style={{ marginTop: "8px", fontSize: "13px" }}>PDF Document Ready</p>
                  </div>
                )}
                <div className={style.previewFooter}>
                  <span className="material-icons-round">check_circle</span>
                  Ready to upload
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className={style.bottomAction}>
        <button className={style.btnCancel} onClick={handleBack}>Cancel</button>
        <button className={style.btnGenerate} onClick={handleSave} disabled={isSaving}>
          <span className="material-icons-round" style={{ fontSize: "18px" }}>save</span>
          {isSaving ? "Saving..." : "Save Rx"}
        </button>
      </div>
    </div>
  );
};
